import { getYear, parseISO } from "date-fns";
import { sortBy, uniq, omit } from "lodash";
import { Financial, Statistic, HistoricQuote } from "@api";
import { ComparisonChartData } from "@components";
import { RangeValue } from "@rentablo/components";
import { useFinancials } from "../useFinancials";
import { useStatistics } from "../useStatistics";
import { getDates } from "./getFromDate";

const merge = (financialData?: Financial[], statisticData?: Statistic[]) => {
  const fYears = financialData?.map(({ year }) => year);
  const sYears = statisticData?.map(({ date }) => getYear(parseISO(date)));
  const years = uniq([...(fYears || []), ...(sYears || [])]);

  return years.map((year) => {
    const financial = financialData?.find((v) => v.year === year);
    const statistic = statisticData?.find(
      (v) => getYear(parseISO(v.date)) === year
    );
    return {
      ...omit(financial, ["year"]),
      ...omit(statistic, ["date"]),
      year,
    };
  });
};

interface Props {
  isin?: string;
  range?: RangeValue;
  historicQuotes?: HistoricQuote[];
}

interface Options {
  enabled?: boolean;
}

export const useChartDataBase = (
  { isin, range }: Props,
  { enabled }: Options = { enabled: true }
) => {
  const { fromDate, toDate, fromYear, toYear } = getDates(range);

  const financialResult = useFinancials(
    {
      isin: isin,
      from: fromYear,
      to: toYear,
    },
    { enabled }
  );

  const statisticResult = useStatistics(
    {
      isin: isin,
      from: fromDate,
      to: toDate,
    },
    { enabled }
  );

  const data: ComparisonChartData[] = sortBy(
    merge(financialResult.data?.financials, statisticResult.data),
    ["year"]
  );

  const isFetching = financialResult.isFetching || statisticResult.isFetching;

  return {
    ...financialResult,
    data,
    securityData: financialResult.data?.security,
    isFetching,
  };
};
